import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { TYPES } from '@/engine/ioc';
import { useInjection } from 'inversify-react';
import type { ViewsService } from '@/engine/app/Views.service';
import { useTheme } from '@mui/material/styles';
import { EyeCheckboxButton, PenCheckboxButton } from '../CheckboxButton.tsx';
import { StyledTableCell } from '../BoxTools';
import type { BasicBoxProps, AngleMeasurementProps } from './types';
import { useHighlight } from '../hooks/useHighlight';
import { useSelect } from '../hooks/useSelect';
import { UpdatableValue } from '../common/UpdatableValue.tsx';
import { getConditionalStyles } from '../styles';
import type { MeasurementBoxesController } from '.';

export const AngleMeasurementBox: React.FC<BasicBoxProps<AngleMeasurementProps>> = (props) => {
  const theme = useTheme();

  const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const measurementVMs = viewsService.currentView?.getMeasurements();

  const { isHighlighted } = useHighlight(measurementVMs);
  const { isSelected } = useSelect(measurementVMs);

  return (
    <TableContainer component={Paper}>
      <Table aria-label="a dense table" size="small">
        <TableBody>
          {props.measurements.map((measurement: AngleMeasurementProps, index) => {
            const isItemSelected = isSelected(measurement.descriptor);
            const isItemHighlighted = isHighlighted(measurement.descriptor);
            return (
              <TableRow
                key={measurement.label}
                data-testid={measurement.descriptor + 'Volume'}
                sx={{
                  ...{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover button.locate-button': {
                      opacity: 1
                    },
                    '& button.locate-button': { opacity: 0.1 }
                  },
                  ...getConditionalStyles(isItemHighlighted, theme, { paddingTop: 0, paddingBottom: 0 })
                }}
                onMouseEnter={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, true)}
                onMouseLeave={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, false)}
                selected={isItemSelected}
                onClick={(): void => {
                  mainController.setMeasurementSelection(measurement.descriptor, !isItemSelected);
                }}
              >
                <TableCell scope="row" key={'label' + index}>
                  {measurement.label}
                </TableCell>
                <TableCell
                  size="small"
                  align="right"
                  key={'value' + index}
                  sx={{ '&': { paddingRight: 0, width: '4rem' } }}
                >
                  <UpdatableValue
                    descriptor={measurement.descriptor}
                    constructor={DT.AngleMeasurement}
                    valueKey="angleDeg"
                  />
                  &deg;
                </TableCell>
                <StyledTableCell align="right" key={'edit' + index} className="limited-width">
                  {measurement.beingEdited !== undefined ? (
                    <PenCheckboxButton
                      onChange={(checked): void => {
                        mainController.setMeasurementBeingEdited(measurement.descriptor, checked);
                      }}
                      checked={measurement.beingEdited}
                      key={'beingEditedBtn' + index}
                    />
                  ) : (
                    <div />
                  )}
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  key={'visibility' + index}
                  className="limited-width"
                  onClick={(event): void => event.stopPropagation()}
                >
                  <EyeCheckboxButton
                    onChange={(checked): void =>
                      mainController.setMeasurementVisibility(measurement.descriptor, checked)
                    }
                    checked={measurement.visible}
                  />
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
